import React, { useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { PickerModel, PickerResponse } from './modelPickerTypes';
import { useAnchoredPortal } from '../../hooks/useAnchoredPortal';
import { useListboxKeyboard } from '../../hooks/useListboxKeyboard';
import { useFilteredList } from '../../hooks/useFilteredList';

interface Props {
  data: PickerResponse;
  active: string | null;
  getAnchorEl: () => HTMLElement | null;
  onPick: (slug: string) => void;
  onToggleFavorite: (slug: string, makeFavorite: boolean) => void;
  onClose: () => void;
}

const SHEET_W = 340;

// Blueprint "construction pattern" — the diagonal hazard texture from the
// artifactory blueprint cards (styles/artifactory.css .construction-pattern).
const BLUEPRINT_PATTERN: React.CSSProperties = {
  backgroundImage:
    'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(0,0,0,0.12) 10px, rgba(0,0,0,0.12) 20px)',
};

const MOD_BADGE: Record<string, string> = { image: '👁', audio: '🎧', video: '📹', file: '📄' };

/** Encode a model slug (which contains '/') into a valid DOM id + CSS/Playwright selector. */
const optId = (slug: string) => `mpicker-opt-${slug.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
const laneBodyId = (key: string) => `mpicker-lane-${key.replace(/[^a-zA-Z0-9_-]/g, '-')}`;

interface RowProps {
  m: PickerModel;
  active: boolean;       // committed selection (slug === active)
  highlighted: boolean;  // keyboard activedescendant
  favored: boolean;
  onPick: (s: string) => void;
  onToggleFavorite: (s: string, f: boolean) => void;
}

function Row({ m, active, highlighted, favored, onPick, onToggleFavorite }: RowProps) {
  const ctx = `${Math.round(m.context_length / 1000)}k`;
  const out = m.max_output_tokens ? ` · out ${Math.round(m.max_output_tokens / 1000)}k` : '';
  const mods = m.input_modalities.map((x) => MOD_BADGE[x]).filter(Boolean).join('');
  return (
    <div className="group flex items-center gap-2 px-2 py-1.5" role="presentation">
      {/* Secondary action — sibling of the option (ARIA forbids interactive descendants of
          role=option); kept out of the Tab order (activedescendant model owns nav). */}
      <button
        type="button"
        data-testid="row-star"
        tabIndex={-1}
        aria-label={favored ? 'Unfavorite' : 'Favorite'}
        onClick={() => onToggleFavorite(m.slug, !favored)}
        className={[
          'flex-shrink-0 text-sm leading-none transition-colors',
          favored ? 'text-amber-400' : 'text-gray-300 hover:text-amber-400 dark:text-gray-600 dark:hover:text-amber-400',
        ].join(' ')}
      >
        ★
      </button>
      <div
        role="option"
        id={optId(m.slug)}
        aria-selected={active}
        tabIndex={-1}
        onClick={() => onPick(m.slug)}
        className={[
          'flex-1 min-w-0 cursor-pointer rounded-md px-1 py-0.5 transition-colors',
          highlighted
            ? 'bg-emerald-50 ring-1 ring-emerald-300 dark:bg-emerald-500/15 dark:ring-emerald-500/40'
            : 'hover:bg-gray-100 dark:hover:bg-gray-700/60',
        ].join(' ')}
      >
        <div className="flex items-center gap-1.5">
          {active && <span className="flex-shrink-0 text-emerald-500" aria-hidden="true">●</span>}
          <span className="truncate text-[13px] font-semibold text-gray-900 dark:text-gray-50">{m.name}</span>
          {m.has_tools && <span className="flex-shrink-0 text-[11px] opacity-70" title="tools">🔧</span>}
          {mods && <span className="flex-shrink-0 text-[11px]" title="modalities">{mods}</span>}
          {m.is_greylisted && <span className="flex-shrink-0 text-amber-500" title="cost-restricted">⚠</span>}
        </div>
        <div className="truncate text-[10px] text-gray-500 dark:text-gray-400">
          {m.provider} · ctx {ctx}{out}
        </div>
      </div>
    </div>
  );
}

function LaneRows({ title, models, rp, open, onToggle }: {
  title: string; models: PickerModel[]; rp: (m: PickerModel) => RowProps;
  open: boolean; onToggle: () => void;
}) {
  if (!models.length) return null;
  return (
    <div className="mb-1.5">
      <button
        type="button"
        tabIndex={-1}
        aria-expanded={open}
        className="w-full flex items-center gap-1 text-left text-[10px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-400 px-1.5 py-1 hover:text-emerald-600 dark:hover:text-emerald-300"
        onClick={onToggle}
      >
        <span className="opacity-60" aria-hidden="true">{open ? '▾' : '▸'}</span>
        <span>{title}</span>
        <span className="text-gray-400 dark:text-gray-500 font-medium normal-case">({models.length})</span>
      </button>
      {open && (
        <div id={laneBodyId(title)} role="group" aria-label={title}>
          {models.map((m) => <Row key={`${title}-${m.slug}`} {...rp(m)} />)}
        </div>
      )}
    </div>
  );
}

const ModelPickerSheet: React.FC<Props> = ({ data, active, getAnchorEl, onPick, onToggleFavorite, onClose }) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const placement = useAnchoredPortal({ getAnchorEl, open: true, width: SHEET_W, align: 'left', preferOpenUp: true });

  const [q, setQ] = useState('');
  const [laneOpen, setLaneOpen] = useState<Record<string, boolean>>({ favorites: true, recents: true, new: true });
  const isLaneOpen = (k: string) => (k.startsWith('prov:') ? !!laneOpen[k] : laneOpen[k] !== false);
  const toggleLane = (k: string) => setLaneOpen((m) => ({ ...m, [k]: !isLaneOpen(k) }));

  const favSet = useMemo(
    () => new Set([...data.favorites.user, ...data.favorites.system].map((m) => m.slug)),
    [data],
  );
  const allProviderModels = useMemo(() => data.providers.flatMap((p) => p.models), [data.providers]);
  const results = useFilteredList(allProviderModels, q,
    (m, query) => (m.name + m.provider + m.slug).toLowerCase().includes(query));

  // Visible-only nav array (collapsed lanes have no DOM rows → exclude).
  const navModels = useMemo<PickerModel[]>(() => {
    if (q) return results;
    const out: PickerModel[] = [];
    if (isLaneOpen('favorites')) out.push(...data.favorites.user, ...data.favorites.system);
    if (isLaneOpen('recents')) out.push(...data.recents);
    if (isLaneOpen('new')) out.push(...data.new);
    for (const p of data.providers) if (isLaneOpen(`prov:${p.provider}`)) out.push(...p.models);
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, results, data, laneOpen]);

  const { activeDescendantId, onKeyDown } = useListboxKeyboard({
    items: navModels, isOpen: true, onCommit: (m) => onPick(m.slug), getItemId: (m) => optId(m.slug),
  });

  const rp = (m: PickerModel): RowProps => ({
    m, active: m.slug === active, highlighted: activeDescendantId === optId(m.slug),
    favored: favSet.has(m.slug), onPick, onToggleFavorite,
  });

  return createPortal(
    <>
      {/* Backdrop: pointer-capture isolation + outside-click close (the disclosure runs with
          closeOnOutsideClick:false so this owns outside-click). */}
      <div className="fixed inset-0 z-[200]" onClick={onClose} />
      <div
        ref={panelRef}
        data-testid="model-picker-sheet"
        onKeyDown={onKeyDown}
        style={{ ...placement?.style, width: SHEET_W, maxHeight: placement?.maxHeight }}
        className="z-[201] flex flex-col overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-2xl ring-1 ring-black/5 dark:ring-white/5"
      >
        {/* Blueprint header band — emerald accent + diagonal hazard texture */}
        <div className="relative px-3 py-2 bg-emerald-600 dark:bg-emerald-700 overflow-hidden flex-shrink-0">
          <div className="absolute inset-0 opacity-20" style={BLUEPRINT_PATTERN} aria-hidden />
          <div className="relative flex items-center gap-2">
            <span className="text-[11px] font-bold uppercase tracking-widest text-white/90">Models</span>
            <input
              autoFocus
              role="combobox"
              aria-expanded
              aria-haspopup="listbox"
              aria-controls="mpicker-listbox"
              aria-activedescendant={activeDescendantId}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search…"
              className="flex-1 min-w-0 px-2 py-1 text-xs rounded-md border border-white/30 bg-white/15 text-white placeholder-white/60 focus:outline-none focus:ring-1 focus:ring-white/60"
            />
          </div>
        </div>

        {/* Lanes */}
        <div role="listbox" id="mpicker-listbox" aria-label="Models" className="overflow-auto p-1.5">
          {!q && (
            <>
              <LaneRows title="⭐ Favorites" models={[...data.favorites.user, ...data.favorites.system]} rp={rp}
                open={isLaneOpen('favorites')} onToggle={() => toggleLane('favorites')} />
              <LaneRows title="🕘 Recents" models={data.recents} rp={rp}
                open={isLaneOpen('recents')} onToggle={() => toggleLane('recents')} />
              <LaneRows title="🆕 New" models={data.new} rp={rp}
                open={isLaneOpen('new')} onToggle={() => toggleLane('new')} />
              {data.providers.map((p) => (
                <LaneRows key={p.provider} title={`🏢 ${p.provider}`} models={p.models} rp={rp}
                  open={isLaneOpen(`prov:${p.provider}`)} onToggle={() => toggleLane(`prov:${p.provider}`)} />
              ))}
            </>
          )}
          {q && (
            <LaneRows title="Results" models={results} rp={rp} open onToggle={() => {}} />
          )}
        </div>
      </div>
    </>,
    document.body,
  );
};

export default ModelPickerSheet;
